import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { ANALYTICS_PATH, REPORT_PATH } from '@constants/routes';
import { MdOutlineFileDownload } from 'react-icons/md';
import { useAppSelector } from '@store/hooks';
import DataLoader from '@components/DataLoader';
import { ADMIN, HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import {
    getReportDeviceData,
    generateDeviceReportPDF,
} from '@utils/hospitalUtils';
import {
    getDataUsingTenantId,
    capitalizeFirstLetter,
} from '@utils/sharedUtils';
import { HOSPITAL_INFO_TABLE } from '@constants/dynamoDBTableName';
import { ReportData } from '@store/reducers/userSlice';
const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

const ReportList = () => {
    const reportDatas: ReportData[] = useAppSelector((state) => state.user.reportDatas || []);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [nurseHospitalName, setNurseHospitalName] = useState<string>('');
    const reportParams = useAppSelector((state) => state.user.reportParams);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const loggedInUserName = useAppSelector(
        (state) => state.login.loggedInUserName
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const loggedInHospitalName = useAppSelector(
        (state) => state.login.loggedInHospitalName
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    // const data = useAppSelector((state) => state.report.changeLogData);
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const filteredUsers = useMemo(() => {
        let filtered = reportDatas;

        if (searchTerm) {
            filtered = filtered.filter(
                (user) =>
                    user.created_at
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.time
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.patient_name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.device_id
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, reportDatas]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt_time);
                const dateB = new Date(b.createdAt_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() === 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() === 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // If createdAt_time is missing, fallback to timing order based on index or keep original
                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;
    const renderPageButton = (pageNumber: any) => {
        return (
            <button
                key={pageNumber}
                className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
                onClick={() => handlePageChange(pageNumber)}
            >
                {pageNumber}
            </button>
        );
    };

    const renderEllipsis = () => {
        return (
            <span
                key="ellipsis"
                className="bg-white pagination-padding py-2 border rounded"
            >
                ...
            </span>
        );
    };
    const backArrow = () => {
        navigate(-1);
    };
    useEffect(() => {
        const fetchDeviceData = async () => {
            try {
                if (loggedInUserRole.toLowerCase() == NURSE.toLowerCase()) {
                    const nurseDatas = await getDataUsingTenantId(
                        HOSPITAL_INFO_TABLE,
                        loggedInUserTenantID
                    );
                    const nurseData = nurseDatas?.Items?.[0];
                    const hospitalName = nurseData?.hospital_name?.S || ' '; // Extract hospital name
                    setNurseHospitalName(hospitalName);
                }
            } catch (error: any) {
                setError(error);
            }
        };
        fetchDeviceData();
    }, []);
    const convertToCSV = (
        data: any[],
        userRole: string,
        hospitalName: string,
        nurseHospitalName: string,
        userName: string
    ) => {
        const csvRows = [];

        // Updated headers
        const headers = [
            'Date',
            'Time',
            'Device Id',
            'Patient Name',
            'Medicine Name',
            'User',
            // ...(userRole.toLowerCase() === HOSPITAL.toLowerCase() ||
            // userRole.toLowerCase() === NURSE.toLowerCase()
            //     ? ['Hospital Name']
            //     : []),
            'Status',
            'Flow Rate',
            'Volume',
            'Drip Quantity',
            'Parameter Name',
            'Old Value',
            'New Value',
            'Additional Parameter',
            'Old Value',
            'New Value',
        ];

        csvRows.push(headers.join(','));

        data.forEach((row) => {
            let displayedName: string = ''; // Explicitly declare displayedName as a string
            let displayHospitalName: string = ''; // For Hospital Name

            if (loggedInUserRole.toLowerCase() === INDIVIDUAL.toLowerCase()) {
                displayedName = loggedInUserName;
            } else if (
                loggedInUserRole.toLowerCase() === HOSPITAL.toLowerCase() ||
                loggedInUserRole.toLowerCase() === NURSE.toLowerCase()
            ) {
                displayedName = row.nurse_name;
                // Show hospital name only for HOSPITAL or NURSE roles
                displayHospitalName =
                    loggedInUserRole.toLowerCase() === HOSPITAL.toLowerCase()
                        ? hospitalName
                        : nurseHospitalName;
            }

            // Initialize variables for parameters
            let mainParameter = { name: '', oldValue: '', newValue: '' };
            let additionalParameter = { name: '', oldValue: '', newValue: '' };

            // Determine which parameters to show
            if (row.additionalValues && row.additionalValues.length > 0) {
                const dripQuantity = row.additionalValues.find(
                    (v: any) => v.name === 'Drip Quantity'
                );
                const flowRate = row.additionalValues.find(
                    (v: any) => v.name === 'Flow Rate'
                );
                const infusionTime = row.additionalValues.find(
                    (v: any) => v.name === 'Infusion Time'
                );

                if (
                    dripQuantity &&
                    (dripQuantity.oldValue !== '' ||
                        dripQuantity.newValue !== '')
                ) {
                    mainParameter = dripQuantity;
                    additionalParameter = flowRate ||
                        infusionTime || {
                            name: '',
                            oldValue: '',
                            newValue: '',
                        };
                } else if (flowRate) {
                    mainParameter = flowRate;
                } else if (infusionTime) {
                    mainParameter = infusionTime;
                }
            }

            // Add a single row combining all the data points
            const values = [
                row.created_at,
                row.time,
                row.device_id,
                row.patient_name,
                row.medicine_name || '',
                displayedName,
                // ...(userRole.toLowerCase() === HOSPITAL.toLowerCase() ||
                // userRole.toLowerCase() === NURSE.toLowerCase()
                //     ? [displayHospitalName]
                //     : []),
                capitalizeFirstLetter(row.status),
                row.rpm || '0',
                row.re_saline || '0',
                row.t_qua || '0',
                mainParameter.name,
                mainParameter.oldValue,
                mainParameter.newValue,
                additionalParameter.name,
                additionalParameter.oldValue,
                additionalParameter.newValue,
            ];

            csvRows.push(values.join(','));
        });

        return csvRows.join('\n');
    };

    const handleExport = async () => {
        if (!reportParams) return;

        setExportLoading(true);
        try {
            const result = await generateDeviceReportPDF({
                device_id: reportParams.device_id,
                patient_name: reportParams.patient_name,
                start_date: reportParams.start_date,
                end_date: reportParams.end_date,
                tenant_id: loggedInUserTenantID
            });

            if (result && result.url) {
                const link = document.createElement('a');
                link.href = result.url;
                // The filename is handled by the server's Content-Disposition header
                link.setAttribute('download', ''); 
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert('Failed to generate report PDF');
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('An error occurred while generating the report');
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <>
            <div className="mx-auto 2xl:max-w-full  mt-1">
                <div className="d-flex justify-content-between align-items-center">
                    <div
                        className="font-bold text-base text-black flex items-center"
                        onClick={backArrow}
                    >
                        {/* <Link to={ANALYTICS_PATH}> */}
                        {/* <div className="" > */}
                        <FaArrowLeftLong className="cursor-pointer" />{' '}
                        <div className="ml-2">{t('report.title')}</div>
                        {/* </div> */}
                        {/* </Link> */}
                    </div>
                    <button
                        className="bg-primary flex justify-content-center align-items-center rounded ml-4 py-2.5 px-3 text-white 2xl:w-36 lg:w-32 mr-3 relative disabled:opacity-50"
                        onClick={handleExport}
                        disabled={exportLoading}
                    >
                        <MdOutlineFileDownload
                            className="absolute left-6"
                            size={20}
                        />
                        {exportLoading ? '...' : t('report.export')}
                    </button>
                </div>
                {/* <div className="card rounded-xl mt-5 p-4 w-full mx-auto "> */}
                <div className="card rounded-xl mt-5 p-0 w-full mx-auto ">
                    <div className="card-datatable table-responsive">
                        <table className="datatables-users table ">
                            <thead className="border-bottom">
                                <tr className="text-black text-xl font-bold py-4 !normal-case">
                                    <th className="p-3 font-bold !normal-case">
                                        {t('date')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('time')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('device_id')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('patient_name')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('medicine_name')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('user')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('status')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('device_details_page.flow_rate')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('device_details_page.volume')}
                                    </th>
                                    <th className="p-3 font-bold !normal-case">
                                        {t('device_details_page.drip_quantity')}
                                    </th>
                                </tr>
                            </thead>
                            {paginatedUsers != null &&
                            paginatedUsers?.length != 0 ? (
                                <tbody>
                                    {paginatedUsers.map((item, index) => {
                                        let displayedName;

                                        if (
                                            loggedInUserRole.toLowerCase() ===
                                            INDIVIDUAL.toLowerCase()
                                        ) {
                                            displayedName = loggedInUserName;
                                        } else if (
                                            loggedInUserRole.toLowerCase() ===
                                                HOSPITAL.toLowerCase() ||
                                            loggedInUserRole.toLowerCase() ===
                                                NURSE.toLowerCase()
                                        ) {
                                            displayedName = item.nurse_name;
                                        }

                                        return (
                                            <tr key={index} className="border">
                                                <td className="text-base font-normal text-table_header">
                                                    {item.created_at}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.time}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.device_id}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.patient_name}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.medicine_name}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {displayedName}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {capitalizeFirstLetter(
                                                        item.status
                                                    )}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.rpm || '0'}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.re_saline || '0'}
                                                </td>
                                                <td className="text-base font-normal text-table_header">
                                                    {item.t_qua || '0'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            ) : (
                                <tbody>
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="text-center p-4"
                                        >
                                            {t('no_report_found')}
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                    </div>
                </div>
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
        </>
    );
};

export default ReportList;
